// ============================================================
// ANSICHTSSACHE-AGENT – Seitenblatt (Slide-over)
// ============================================================
// Ein Blatt, das von rechts hereinfaehrt und den Kontext stehen
// laesst. Es ersetzt den Seitenwechsel dort, wo man ein Ding
// ansieht, ohne die Liste zu verlassen — eine Aufgabe aus einer
// Aufgabenliste, ein Kunde aus einer Kundenliste.
//
// Warum nicht selbst gebaut: ein Blatt, das ueber dem Inhalt
// liegt, muss mehr koennen als hereinfahren — den Fokus einfangen
// und beim Schliessen zurueckgeben, den Hintergrund fuer
// Screenreader ausblenden, Escape bedienen, das Scrollen der
// Seite darunter anhalten. Radix macht das; die Bewegung kommt
// von uns.
//
// `forceMount` plus `AnimatePresence`: ohne das nimmt Radix das
// Blatt sofort aus dem Baum und die Ausfahrt waere nie zu sehen.
//
// `m` statt `motion`: die Bewegungsbausteine kommen ueber
// `LazyMotion` nach — dasselbe Stueck, das die globale Suche in
// der Kopfzeile jeder Seite schon laedt (`layout/motion-features`).
// Mit `motion` laege das ganze Paket samt Zieh- und Layout-
// Animationen in jedem Buendel, das ein Blatt oeffnen kann.
//
// Gestalt: eine Karte, die von rechts angelegt wird. Oben und
// unten ein schmaler Abstand zum Bildrand, links runde Ecken,
// rechts buendig und eckig — dort schliesst sie an den Rand an,
// aus dem sie kam. Dahinter wird die Seite abgedunkelt und leicht
// verwischt, damit klar ist, worauf gerade der Blick liegt.
// ============================================================

"use client";

import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, LazyMotion, m } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const ladeBewegung = () =>
  import("@/components/layout/motion-features").then((modul) => modul.default);

export function Seitenblatt({
  offen,
  onSchliessen,
  titel,
  kopfZusatz,
  breite = "560px",
  inhaltClassName,
  children,
}: {
  offen: boolean;
  onSchliessen: () => void;
  /** Für Screenreader immer nötig, auch wenn oben schon etwas steht. */
  titel: string;
  kopfZusatz?: React.ReactNode;
  breite?: string;
  /**
   * Klassen fuer den Inhaltsbereich. Ohne Angabe scrollt er und
   * haelt Abstand zum Rand (`px-6`, wie der Kopf) — das passt fuer
   * Formulare. Ein Blatt, dessen Inhalt die ganze Flaeche fuellen
   * soll (eine Vorschau, eine Karte), setzt hier
   * `flex min-h-0 flex-1 flex-col p-0` und haelt den Rand selbst.
   */
  inhaltClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <Dialog.Root open={offen} onOpenChange={(o) => !o && onSchliessen()}>
      <LazyMotion features={ladeBewegung}>
        <AnimatePresence>
          {offen && (
            <Dialog.Portal forceMount>
              <Dialog.Overlay asChild forceMount>
                <m.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, ease: [0.2, 0, 0, 1] }}
                  className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[4px] dark:bg-black/50"
                />
              </Dialog.Overlay>

              <Dialog.Content
                asChild
                forceMount
                // Escape gehoert zuerst dem Feld, in dem gerade
                // geschrieben wird: ein Tastendruck, der den Entwurf
                // verwirft UND das Blatt zuschlaegt, nimmt zwei
                // Entscheidungen auf einmal ab. Das zweite Escape
                // schliesst dann.
                onEscapeKeyDown={(e) => {
                  const el = document.activeElement;
                  const imFeld =
                    el instanceof HTMLTextAreaElement ||
                    el instanceof HTMLInputElement ||
                    // Der Markdown-Editor ist ein `contenteditable`,
                    // kein Textfeld — er zaehlt genauso.
                    (el instanceof HTMLElement && el.isContentEditable);
                  if (imFeld) e.preventDefault();
                }}
              >
                <m.div
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  // Feder statt Blende: ein Blatt mit Masse faehrt
                  // herein und kommt zur Ruhe, es blendet nicht auf.
                  transition={{ type: "spring", stiffness: 380, damping: 38, mass: 0.8 }}
                  // Auch auf dem Telefon bleibt links ein Streifen
                  // Seite stehen — sonst ist es keine Karte mehr.
                  style={{ width: `min(${breite}, calc(100vw - 0.75rem))` }}
                  className={cn(
                    // `overflow-hidden`, damit eine randlose Vorschau
                    // die runden Ecken nicht ueberdeckt. Gerollt wird
                    // im Inhaltsbereich, nicht hier.
                    "fixed inset-y-3 right-0 z-50 flex flex-col overflow-hidden rounded-l-2xl",
                    // Das Blatt selbst ist kein Bedienelement. Radix gibt
                    // ihm den Fokus nur als Rueckfall — etwa wenn ein Feld
                    // mit Enter zugeht und verschwindet —, und der rote
                    // Ring der Tastaturbedienung lag dann um das ganze Blatt.
                    "outline-none",
                    // `shadow-sheet` statt `e4`: der traegt auch zur
                    // Seite, und nach links soll die Karte abheben.
                    // Der Grund ist seit dem 29.09.2026 ganz leicht grau
                    // (`bg-panel`), damit weisse Karten im Blatt als
                    // Karten zu erkennen sind.
                    "bg-bg-panel shadow-sheet",
                    "ring-1 ring-black/[0.06] dark:ring-white/[0.08]",
                  )}
                >
                  <header className="border-border-subtle flex shrink-0 items-start justify-between gap-3 border-b px-6 py-4">
                    <div className="min-w-0 flex-1">
                      <Dialog.Title className="sr-only">{titel}</Dialog.Title>
                      {kopfZusatz}
                    </div>
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        aria-label="Schließen"
                        className="text-text-tertiary duration-fast hover:bg-bg-secondary hover:text-text-primary grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors"
                      >
                        <X className="h-4 w-4" aria-hidden />
                      </button>
                    </Dialog.Close>
                  </header>

                  <div className={cn("min-h-0 flex-1 overflow-y-auto px-6 py-5", inhaltClassName)}>
                    {children}
                  </div>
                </m.div>
              </Dialog.Content>
            </Dialog.Portal>
          )}
        </AnimatePresence>
      </LazyMotion>
    </Dialog.Root>
  );
}
